import React from "react";
import { Link } from "react-router-dom";
import ChatInfo from "./ChatInfo";
import { defaultPfpUrl, defaultGroupPfpUrl, formatLastActive } from "../../utils";
import { memberLastOnline, recomputeActiveNowCount } from "./presenceUtils";
import { useNow } from "../../../hooks/useNow";
import { useUserRealtime } from "../../../context/UserRealtimeContext";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";

/**
 * Optional controls when ChatHeader is embedded in the floating messages dock
 * or when the full messaging page needs a mobile back control.
 * @typedef {Object} ChatHeaderPanelActions
 * @property {() => void} [onBack]
 * @property {() => void} [onExpand]
 * @property {() => void} [onClose]
 * @property {string} [backClassName] - Extra classes for the back button (e.g. md:hidden)
 */

/**
 * ChatHeader component - Displays chat information and actions
 * @param {Object} props
 * @param {number} props.chatId - Chat ID
 * @param {Object} props.chatInfo - Chat information
 * @param {function} props.onChatInfoUpdate - Callback to update chat info
 * @param {number} props.userId - Current user ID
 * @param {ChatHeaderPanelActions} [props.panelActions] - Dock panel: back / full page / dismiss
 */
function ChatHeader({
  chatId,
  chatInfo,
  onChatInfoUpdate,
  userId,
  panelActions,
  orgChannelOptions,
  currentUserPfp,
}) {
    const [showChatInfo, setShowChatInfo] = React.useState(false);
    const now = useNow();
    const { presenceByUserId } = useUserRealtime();

    const hasBack = panelActions?.onBack != null;
    const isDockPanel =
        panelActions?.onExpand != null || panelActions?.onClose != null;

    const panelActionButtons = isDockPanel ? (
        <>
            {panelActions.onExpand && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-text-muted hover:text-text-secondary"
                    onClick={panelActions.onExpand}
                    title="Open full messaging"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                </Button>
            )}
            {panelActions.onClose && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-text-muted hover:text-text-secondary"
                    onClick={panelActions.onClose}
                    title="Close"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Button>
            )}
        </>
    ) : null;

    const backButton = hasBack ? (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`shrink-0 text-text-muted hover:text-text-secondary ${panelActions.backClassName || ""}`}
            onClick={panelActions.onBack}
            title="Back"
        >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
        </Button>
    ) : null;

    if (!chatInfo) {
        return (
            <div className="flex items-center justify-between border-b border-border bg-secondary p-4">
                <div className="flex min-w-0 flex-1 items-center gap-1">
                    {backButton}
                    <div className="flex min-w-0 items-center">
                        <Skeleton className="mr-3 h-10 w-10 shrink-0 rounded-full" />
                        <div className="min-w-0">
                            <div className="font-medium text-text-primary">Loading...</div>
                            <div className="text-sm text-text-muted">Loading chat info</div>
                        </div>
                    </div>
                </div>
                <div className="flex shrink-0 items-center space-x-1">
                    {panelActionButtons}
                    {!isDockPanel && (
                        <button type="button" className="text-text-muted hover:text-text-secondary">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const isGroupChat =
        chatInfo.chat_type === "group" || chatInfo.type === "group";
    const dmPeerUserId =
        !isGroupChat && Array.isArray(chatInfo.other_members)
            ? chatInfo.other_members[0]?.user_id
            : null;
    const dmPeerProfilePath =
        dmPeerUserId !== undefined && dmPeerUserId !== null
            ? `/profile/${dmPeerUserId}`
            : null;

    const groupSubtitle = `${chatInfo.member_count || 0} members · ${recomputeActiveNowCount(chatInfo, now, presenceByUserId)} active now`;
    const dmSubtitle = formatLastActive(
        memberLastOnline(
            Array.isArray(chatInfo.other_members) ? chatInfo.other_members[0] : null,
            presenceByUserId
        ),
        now
    );

    const headerIdentity = (
        <>
            <Avatar className="mr-3 h-10 w-10">
                <AvatarImage
                    src={chatInfo.image_url || (isGroupChat ? defaultGroupPfpUrl : defaultPfpUrl)}
                    alt={chatInfo.name}
                    onError={(e) => { e.target.src = defaultGroupPfpUrl; }}
                />
                <AvatarFallback>
                    {(chatInfo.name || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
                <div className="truncate font-medium text-text-primary">{chatInfo.name}</div>
                <div className="text-sm text-text-muted">
                    {isGroupChat ? groupSubtitle : dmSubtitle}
                </div>
            </div>
        </>
    );

    return (
        <>
            <div className="flex items-center justify-between border-b border-border bg-secondary p-4">
                <div className="flex min-w-0 flex-1 items-center gap-1">
                    {backButton}
                    <div className="flex min-w-0 items-center">
                        {dmPeerProfilePath ? (
                            <Link
                                to={dmPeerProfilePath}
                                className="flex min-w-0 max-w-full items-center rounded-lg outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
                                aria-label={`View ${chatInfo.name || "user"}'s profile`}
                            >
                                {headerIdentity}
                            </Link>
                        ) : (
                            <div className="flex min-w-0 max-w-full items-center">{headerIdentity}</div>
                        )}
                    </div>
                </div>

                <div className="flex shrink-0 items-center space-x-1">
                    {panelActionButtons}
                    {!isDockPanel && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-text-muted hover:text-text-secondary"
                            onClick={() => setShowChatInfo(true)}
                            title="Chat info"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </Button>
                    )}
                </div>
            </div>

            {showChatInfo && (
                <ChatInfo 
                    chatId={chatId}
                    chatInfo={chatInfo}
                    userId={userId}
                    onClose={() => setShowChatInfo(false)}
                    onChatInfoUpdate={onChatInfoUpdate}
                    orgChannelOptions={orgChannelOptions}
                    currentUserPfp={currentUserPfp}
                />
            )}
        </>
    );
}

export default ChatHeader;
